<!----------------- BEGIN TS ---------------------->
<script lang="ts">
import { initPopover } from "@/plugins/bootstrapSetup";
import { Popover } from "bootstrap";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import {
  extractScreenshot,
  downloadFromSrc,
  makeSafeFileName,
} from "@/helpers";
import { VideoJsPlayer } from "video.js";
import { VideoInfo } from "@/types";

@Component({})
export default class TheAccessControlToggleButton extends Vue {
  // ---------- Props ----------

  /** The currently active player (since the players swap, this changes as videos come in) */
  @Prop() activePlayer!: VideoJsPlayer;

  /** The video info defined in the camio_viewer.js file. */
  @Prop() videoInfo!: VideoInfo;

  /** Whether or not the viewer is in restricted mode. */
  @Prop() isViewerRestricted!: boolean | undefined;

  // ------- Local Vars --------

  /** Reference to all popovers so we can modify them (Note that in this component, there is only one). */
  popover!: Popover[];

  // --------- Watchers --------

  // ------- Lifecycle ---------

  mounted(): void {
    if (this.$refs.screenshotButton) {
      this.popover = initPopover(
        this.$refs.screenshotButton as Element,
        undefined,
        false
      );
    }
    window.addEventListener("keyup", this.handleKeyUpEvent);
  }

  beforeDestroy() {
    window.removeEventListener("keyup", this.handleKeyUpEvent);
  }
  // --------- Methods ---------

  /** Choose an icon based on the state. */
  get chooseIcon(): string[] {
    return ["camera"];
  }

  /** Gets the help message. */
  get dynamicHelpMessage(): string {
    return "Screenshot the current frame.";
  }

  /** Saves the screenshot using helper functions in helpers.ts */
  saveScreenshot(): void {
    // Only save if it is not a restricted viewer
    if (this.isViewerRestricted === false) {
      const videoElement = this.activePlayer.el()
        .children[0] as HTMLVideoElement;
      const currentTime = this.videoInfo.startDate.add(
        this.activePlayer.currentTime(),
        "seconds"
      );
      const fileName = makeSafeFileName(
        `screenshot-${this.videoInfo.cameraName}-${this.videoInfo.userAlias}-${currentTime}`
      );

      const screenshotBase64 = extractScreenshot(videoElement, "png") as string;

      downloadFromSrc("png", fileName, screenshotBase64);
    }
  }

  /** Handles the keypress event for advancing frames */
  handleKeyUpEvent(e: KeyboardEvent): void {
    const element = e.target as HTMLElement;
    // Ignore any keypresses that are in input fields and stuff
    if (element.nodeName === "BODY") {
      if (e.key == "s") {
        this.saveScreenshot();
      }
    }
  }
}
</script>
<!----------------- END TS ------------------------>

<!----------------- BEGIN HTML -------------------->
<template lang="html">
  <div class="the-screenshot-button-container" ref="screenshotButton">
    <button
      type="button"
      class="btn btn-light screenshot-button text-dark"
      @click="saveScreenshot"
      data-bs-toggle="popover"
      data-bs-trigger="hover"
      data-bs-container="#app-container"
      data-bs-html="true"
      :data-bs-content="`<span>${dynamicHelpMessage}</span> <kbd>s</kbd>`"
    >
      <b-iconstack class="icon" font-scale="2">
        <b-icon
          stacked
          v-for="(icon, index) in chooseIcon"
          :key="`icon-${index}`"
          :icon="icon"
          :scale="index > 0 ? '2.5' : '.85'"
        ></b-icon>
      </b-iconstack>
    </button>
  </div>
</template>
<!----------------- END HTML ---------------------->

<!----------------- BEGIN SCSS -------------------->
<style scoped lang="scss">
// Override SCSS Classes here
.the-screenshot-button-container {
  display: flex;
  margin-left: auto;

  .screenshot-button {
    box-shadow: 4px 7px 4px 0px rgb(0 0 0 / 25%);
    border-radius: 0px 0px 10px 10px;
    cursor: pointer;
    font-size: 14px;
    max-height: 71px;

    .icon {
      width: 30px;
      @media only screen and (max-width: 600px),
        screen and (max-height: 550px) {
        width: 20px;
      }

      @media only screen and (max-width: 500px),
        screen and (max-height: 400px) {
        width: 10px;
      }
    }
  }
}
</style>
<!----------------- END SCSS ---------------------->
